import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, CreditCard, Repeat } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [10, 25, 50, 100];

export default function DonateDialog({ open, onClose }) {
  const { t, lang } = useLang();
  const [freq, setFreq] = useState("once");
  const [selected, setSelected] = useState(25);
  const [isCustom, setIsCustom] = useState(false);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const customAmount = Number(custom);
    if (isCustom && (!custom || isNaN(customAmount) || customAmount < 2)) {
      toast.error(t.donate.invalid);
      return;
    }
    setLoading(true);
    // Inside sandboxed iframes (editor preview) top-navigation is blocked:
    // open the payment tab synchronously on click, then point it at Stripe.
    const inIframe = window.top && window.top !== window.self;
    let payWindow = null;
    if (inIframe) {
      try {
        payWindow = window.open("about:blank", "_blank");
        if (payWindow) {
          const pdoc = payWindow.document;
          pdoc.body.style.cssText =
            "background:#04132e;color:#fff;font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0";
          pdoc.body.textContent = "Reindirizzamento al pagamento sicuro…";
        }
      } catch {
        payWindow = null;
      }
    }
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: isCustom
          ? "donation_custom"
          : freq === "monthly"
            ? `donation_monthly_${selected}`
            : `donation_${selected}`,
        custom_amount: isCustom ? customAmount : undefined,
        recurring: freq === "monthly",
        origin_url: window.location.origin,
        language: lang,
      });
      const url = res.data.checkout_url;
      if (payWindow) {
        payWindow.location.href = url;
        setLoading(false);
        onClose();
      } else if (inIframe) {
        try {
          window.top.location.href = url;
        } catch {
          window.location.href = url;
        }
      } else {
        window.location.href = url;
      }
    } catch {
      if (payWindow) {
        try {
          payWindow.close();
        } catch (err) {
          console.error("Failed to close payment popup:", err);
        }
      }
      toast.error(t.donate.error);
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#020b1c]/80 backdrop-blur-sm"
          />
          <motion.div
            data-testid="donate-dialog"
            initial={{ opacity: 0, y: 50, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/10 bg-[#071d40] p-8 shadow-2xl md:p-10"
          >
            <button
              data-testid="donate-close-button"
              onClick={onClose}
              className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-white/15 text-white/60 transition-colors hover:text-white"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <h3 className="font-serif text-3xl tracking-tight text-white md:text-4xl">
              {t.donate.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/60">{t.donate.sub}</p>

            <div className="mt-7 grid grid-cols-2 gap-1 rounded-full border border-white/15 p-1" data-testid="freq-toggle">
              <button
                data-testid="freq-once-button"
                onClick={() => setFreq("once")}
                className={`flex items-center justify-center gap-2 rounded-full py-3 text-[12px] font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${
                  freq === "once" ? "bg-primary text-white" : "text-white/55 hover:text-white"
                }`}
              >
                {t.donate.once}
              </button>
              <button
                data-testid="freq-monthly-button"
                onClick={() => setFreq("monthly")}
                className={`flex items-center justify-center gap-2 rounded-full py-3 text-[12px] font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${
                  freq === "monthly" ? "bg-primary text-white" : "text-white/55 hover:text-white"
                }`}
              >
                <Repeat size={13} />
                {t.donate.monthly}
              </button>
            </div>

            <AnimatePresence>
              {freq === "monthly" && (
                <motion.p
                  data-testid="monthly-hint"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden pt-3 text-center text-[12px] text-white/50"
                >
                  {t.donate.monthlyHint}
                </motion.p>
              )}
            </AnimatePresence>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {PRESETS.map((v) => (
                <button
                  key={v}
                  data-testid={`amount-preset-${v}`}
                  onClick={() => {
                    setSelected(v);
                    setIsCustom(false);
                  }}
                  className={`rounded-2xl border px-4 py-5 font-serif text-2xl transition-all duration-300 ${
                    !isCustom && selected === v
                      ? "border-primary bg-primary/15 text-white"
                      : "border-white/15 bg-white/[0.04] text-white/60 hover:border-white/40 hover:text-white"
                  }`}
                >
                  € {v}
                  {freq === "monthly" && (
                    <span className="block text-[11px] font-sans uppercase tracking-[0.14em] text-white/45">
                      /{t.donate.monthly.toLowerCase().replace("ogni ", "").replace("every ", "mo")}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <button
              data-testid="amount-custom-toggle"
              onClick={() => setIsCustom(true)}
              className={`mt-3 w-full rounded-2xl border px-4 py-4 text-left text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${
                isCustom
                  ? "border-primary bg-primary/15 text-white"
                  : "border-white/15 bg-white/[0.04] text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {t.donate.custom}
            </button>

            <AnimatePresence>
              {isCustom && (
                <motion.input
                  data-testid="amount-custom-input"
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.3 }}
                  type="number"
                  min="2"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder={t.donate.customPlaceholder}
                  className="w-full rounded-2xl border border-white/15 bg-white/[0.05] px-5 py-4 text-lg text-white placeholder:text-white/35 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                />
              )}
            </AnimatePresence>

            <button
              data-testid="donate-submit-button"
              onClick={submit}
              disabled={loading}
              className="mt-8 w-full rounded-full bg-primary px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white shadow-lg shadow-primary/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e0562a] disabled:translate-y-0 disabled:opacity-60"
            >
              {loading ? t.donate.processing : t.donate.submit}
            </button>

            <div className="mt-5 flex items-center justify-center gap-4 text-[11px] uppercase tracking-[0.16em] text-white/40">
              <span className="flex items-center gap-1.5">
                <Lock size={12} /> {t.donate.secure.split(".")[0]}
              </span>
              <span className="flex items-center gap-1.5">
                <CreditCard size={12} /> {t.donate.methods}
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
